import React, { useState, useEffect } from 'react'
import { 
  Activity, 
  Users, 
  Globe, 
  MousePointerClick,
  Smartphone,
  Monitor,
  Tablet,
  Loader,
  AlertCircle,
  TrendingUp,
  MapPin,
  Eye
} from 'lucide-react'
import { analyticsAPI, formatters } from '../services/analytics'

function Realtime() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(new Date())

  useEffect(() => {
    fetchRealtimeData()
    
    // Atualizar dados a cada 30 segundos
    const interval = setInterval(() => {
      fetchRealtimeData()
    }, 30000)

    return () => clearInterval(interval)
  }, [])

  const fetchRealtimeData = async () => {
    try {
      setError(null)
      const realtime = await analyticsAPI.getRealtime()
      setData(realtime)
      setLastUpdate(new Date())
    } catch (err) {
      setError(err.message)
      console.error('Erro ao buscar dados em tempo real:', err)
    } finally {
      setLoading(false)
    }
  }

  const getDeviceIcon = (device) => {
    switch (device.toLowerCase()) {
      case 'desktop': return <Monitor className="w-5 h-5 text-blue-600" />
      case 'mobile': return <Smartphone className="w-5 h-5 text-green-600" />
      case 'tablet': return <Tablet className="w-5 h-5 text-purple-600" />
      default: return <Monitor className="w-5 h-5 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'high': return 'text-green-600 bg-green-100'
      case 'medium': return 'text-yellow-600 bg-yellow-100'
      case 'low': return 'text-orange-600 bg-orange-100'
      case 'none': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'high': return 'Alto Tráfego'
      case 'medium': return 'Tráfego Moderado'
      case 'low': return 'Tráfego Baixo'
      case 'none': return 'Sem Tráfego'
      default: return 'Desconhecido'
    }
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2">
          <Loader className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-lg text-gray-600">Carregando dados em tempo real...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-lg font-medium text-red-800 mb-2">Erro ao carregar dados</h3>
        <p className="text-red-600">{error}</p>
        <button 
          onClick={fetchRealtimeData}
          className="mt-4 btn-primary"
        >
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header com status */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dados em Tempo Real</h1>
          <p className="mt-2 text-gray-600">Monitoramento ao vivo dos usuários</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse mr-2"></div>
            <span className="text-sm text-gray-600">
              Atualizado: {formatTimestamp(lastUpdate)}
            </span>
          </div>
          <button
            onClick={fetchRealtimeData}
            className="btn-secondary text-sm"
            disabled={loading}
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Usuários ativos - destaque principal */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full text-white shadow-lg">
          <div className="text-center">
            <div className="text-3xl font-bold">
              {data?.activeUsers?.total || 0}
            </div>
            <div className="text-sm opacity-90">usuários ativos</div>
          </div>
        </div>
        <div className="mt-4">
          <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
            getStatusColor(data?.activeUsers?.status)
          }`}>
            {getStatusText(data?.activeUsers?.status)}
          </span>
        </div>
      </div>

      {/* Alertas */}
      {data?.summary?.alerts && data.summary.alerts.length > 0 && (
        <div className="space-y-2">
          {data.summary.alerts.map((alert, index) => (
            <div key={index} className={`p-4 rounded-lg border ${
              alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200' :
              alert.type === 'info' ? 'bg-blue-50 border-blue-200' :
              'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center">
                <AlertCircle className={`w-5 h-5 mr-2 ${
                  alert.type === 'warning' ? 'text-yellow-600' :
                  alert.type === 'info' ? 'text-blue-600' :
                  'text-gray-600'
                }`} />
                <span className="font-medium">{alert.message}</span>
                {alert.value && (
                  <span className="ml-2 text-sm text-gray-600">
                    ({formatters.number(alert.value)})
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Métricas principais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4">
            <Globe className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">País Principal</h3>
          <p className="text-2xl font-bold text-blue-600 mt-2">
            {data?.summary?.topCountry || 'N/A'}
          </p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mx-auto mb-4">
            <Eye className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Página Mais Vista</h3>
          <p className="text-sm font-medium text-green-600 mt-2 truncate">
            {data?.summary?.topPage || 'N/A'}
          </p>
        </div>

        <div className="card text-center">
          <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mx-auto mb-4">
            <TrendingUp className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Fonte Principal</h3>
          <p className="text-2xl font-bold text-purple-600 mt-2">
            {data?.summary?.topTrafficSource || 'N/A'}
          </p>
        </div>
      </div>

      {/* Dados detalhados em grade */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Usuários por País */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <MapPin className="w-5 h-5 mr-2" />
            Usuários por País
          </h3>
          <div className="space-y-4">
            {data?.usersByCountry?.map((country, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center">
                  <span className="text-lg mr-2">{country.countryCode === 'BR' ? '🇧🇷' : '🌍'}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {country.country}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="w-16 bg-gray-200 rounded-full h-2 mr-3">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.min(100, (country.activeUsers / (data?.usersByCountry?.[0]?.activeUsers || 1)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm font-bold text-gray-900 w-8 text-right">
                    {country.activeUsers}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Páginas Populares */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Eye className="w-5 h-5 mr-2" />
            Páginas Mais Visitadas
          </h3>
          <div className="space-y-4">
            {data?.topPages?.map((page, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {page.title}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {page.pagePath}
                  </p>
                </div>
                <div className="flex items-center space-x-3 ml-4">
                  <div className="text-center">
                    <div className="text-sm font-bold text-gray-900">{page.activeUsers}</div>
                    <div className="text-xs text-gray-500">usuários</div>
                  </div>
                  <div className="text-center">
                    <div className="text-sm font-bold text-blue-600">{page.pageViews}</div>
                    <div className="text-xs text-gray-500">views</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dispositivos */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Monitor className="w-5 h-5 mr-2" />
            Dispositivos
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {data?.devices?.map((device, index) => (
              <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-center mb-2">
                  {getDeviceIcon(device.device)}
                </div>
                <h4 className="font-medium text-gray-900 capitalize">{device.device}</h4>
                <p className="text-lg font-bold text-gray-900 mt-1">{device.activeUsers}</p>
                <p className="text-sm text-gray-500">{device.percentage}%</p>
              </div>
            ))}
          </div>
        </div>

        {/* Fontes de Tráfego */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2" />
            Fontes de Tráfego
          </h3>
          <div className="space-y-4">
            {data?.trafficSources?.map((source, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {source.channel}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {source.source} / {source.medium}
                  </p>
                </div>
                <span className="text-sm font-bold text-gray-900 ml-4">
                  {source.activeUsers}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Eventos Recentes */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Activity className="w-5 h-5 mr-2" />
          Eventos Recentes
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.recentEvents?.map((event, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{event.eventName}</h4>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  event.category === 'E-commerce' ? 'bg-green-100 text-green-800' :
                  event.category === 'Conversion' ? 'bg-blue-100 text-blue-800' :
                  event.category === 'Engagement' ? 'bg-purple-100 text-purple-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {event.category}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Eventos:</span>
                <span className="font-medium text-gray-900">{formatters.number(event.eventCount)}</span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatTimestamp(event.timestamp)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer com informações */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumo da Atividade</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Nível de Engajamento:</span>
              <span className={`ml-2 font-medium ${
                data?.summary?.engagement === 'high' ? 'text-green-600' :
                data?.summary?.engagement === 'medium' ? 'text-yellow-600' :
                'text-red-600'
              }`}>
                {data?.summary?.engagement === 'high' ? 'Alto' :
                 data?.summary?.engagement === 'medium' ? 'Médio' : 'Baixo'}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Total de Usuários Ativos:</span>
              <span className="ml-2 font-bold text-blue-600">
                {formatters.number(data?.summary?.totalActiveUsers || 0)}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Última Atualização:</span>
              <span className="ml-2 font-medium text-gray-900">
                {data?.lastUpdated ? formatters.dateTime(data.lastUpdated) : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Realtime 